import { useRef } from 'react';
import { extractApiErrorMessage } from '@features/billing-readiness/utils/errorUtils';
import { useAutoSave } from '@hooks/useAutoSave';
import { useSnackbar } from 'notistack';

import { OrganizationManagedBillingCard } from '../components/organization-managed-billing-card';
import {
  useJotBillManagedBillingQuery,
  useUpdateJotBillManagedBillingMutation,
} from '../store/hooks';

interface OrganizationManagedBillingCardContainerProps {
  organizationId: string;
}

/**
 * Loads and saves the organization's managed-billing setting. A change saves
 * immediately; the saved value comes back through the query cache. Only the
 * current organization's result is shown (`currentData`), so switching
 * organizations never presents the previous one's choice as editable.
 */
export function OrganizationManagedBillingCardContainer({
  organizationId,
}: OrganizationManagedBillingCardContainerProps) {
  const saving = useRef(false);
  const { enqueueSnackbar } = useSnackbar();
  const { currentData, isFetching, isError, refetch } = useJotBillManagedBillingQuery({
    organizationId,
  });
  const [updateManagedBilling] = useUpdateJotBillManagedBillingMutation();

  const { status, saveImmediatelyAndWait } = useAutoSave<boolean>({
    onSave: async managed => {
      await updateManagedBilling({
        organizationId,
        updateJotBillManagedBillingRequest: { managed },
      }).unwrap();
    },
  });

  const changeManaged = async (managed: boolean) => {
    if (saving.current) return;
    saving.current = true;
    try {
      await saveImmediatelyAndWait(managed);
    } catch (error) {
      enqueueSnackbar(extractApiErrorMessage(error, 'Failed to save the billing service'), {
        variant: 'error',
      });
    } finally {
      saving.current = false;
    }
  };

  return (
    <OrganizationManagedBillingCard
      managed={currentData?.managed}
      updatedAt={currentData?.updated_at}
      updatedBy={currentData?.updated_by}
      isLoading={currentData === undefined && isFetching}
      loadFailed={currentData === undefined && !isFetching && isError}
      onRetry={() => void refetch()}
      saveStatus={status}
      onChange={managed => void changeManaged(managed)}
    />
  );
}
